import { getCentroCustoAtual } from '@acme/common'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Ajuste, NivelAjuste } from './niveis-agrupamento'

dayjs.extend(utc)

export interface LinhaExplodidaAjuste extends Ajuste {
    _centroCustoNome: string
    _localNome: string
    _funcionarioNome: string
    _dataLabel: string
}

function extrairDataAjuste(linha: Ajuste): dayjs.Dayjs {
    const raw =
        (linha as { dataInicial?: string | Date }).dataInicial ||
        linha.data ||
        ''
    if (raw instanceof Date) return dayjs.utc(raw)
    const str = String(raw).split(' - ')[0].trim()
    const ddmmyyyy = str.match(/^(\d{2})\/(\d{2})\/(\d{4})/)
    if (ddmmyyyy) {
        const [, d, m, y] = ddmmyyyy
        return dayjs.utc(`${y}-${m}-${d}`)
    }
    const d = dayjs.utc(str)
    return d.isValid() ? d : dayjs.utc()
}

/**
 * Normaliza cada ajuste com chaves físicas de agrupamento.
 * Mapeamento 1:1 estrito sem fan-out (não duplica linhas de ajuste).
 */
export function explodirLinhas(
    linhas: Ajuste[],
    _niveisAtivos?: Set<NivelAjuste>,
): LinhaExplodidaAjuste[] {
    return (linhas ?? []).map(linha => {
        const funcionario = linha.Funcionario
        const vinculosCC = (
            Array.isArray(funcionario?.FuncionarioCentroCusto)
                ? funcionario.FuncionarioCentroCusto
                : [funcionario?.FuncionarioCentroCusto].filter(Boolean)
        ) as Array<{
            centroCustoId?: number
            dtInicial?: string | Date
            dtFinal?: string | Date | null
            CentroCusto?: {
                id?: number
                codigo?: string
                nomeCentroCusto?: string
                predecessorId?: number
            }
        }>

        const dataReferencia = extrairDataAjuste(linha)
        const vinculosSeguros = vinculosCC.map(v => {
            const centroCusto = v.CentroCusto
                ? {
                      codigo: v.CentroCusto.codigo ?? '',
                      nomeCentroCusto: v.CentroCusto.nomeCentroCusto ?? '',
                      predecessorId: v.CentroCusto.predecessorId ?? 0,
                  }
                : undefined

            return {
                centroCustoId: v.centroCustoId ?? v.CentroCusto?.id ?? 0,
                dtInicial: v.dtInicial ?? '',
                dtFinal: v.dtFinal,
                CentroCusto: centroCusto,
            }
        })

        const ativosCC = funcionario?.FuncionarioCentroCusto
            ? getCentroCustoAtual(
                  { FuncionarioCentroCusto: vinculosSeguros },
                  dataReferencia,
              )
            : []

        const ativosFiltrados = ativosCC.filter(
            cc =>
                !cc.dtInicial ||
                !dayjs.utc(cc.dtInicial).isAfter(dataReferencia, 'day'),
        )

        const cc =
            (ativosFiltrados[0] ?? ativosCC[0])?.CentroCusto ??
            vinculosCC[0]?.CentroCusto

        const vinculosLocais = (
            Array.isArray(funcionario?.FuncionarioLocais)
                ? funcionario.FuncionarioLocais
                : [funcionario?.FuncionarioLocais].filter(Boolean)
        ) as Array<{
            localId?: number
            Local?: { id?: number; nomeLocal?: string }
        }>
        const localFunc = vinculosLocais[0]?.Local

        const centroCustoNome =
            (linha as { _centroCustoNome?: string })._centroCustoNome ||
            (linha as { centroCustoNome?: string }).centroCustoNome ||
            cc?.nomeCentroCusto ||
            ((linha as { centroCustoId?: number }).centroCustoId
                ? `Centro de custo ${(linha as { centroCustoId?: number }).centroCustoId}`
                : 'Sem centro de custo')

        const localNome =
            (linha as { _localNome?: string })._localNome ||
            (linha as { localNome?: string }).localNome ||
            (linha as { Local?: { nomeLocal?: string } }).Local?.nomeLocal ||
            localFunc?.nomeLocal ||
            ((linha as { localId?: number }).localId
                ? `Local ${(linha as { localId?: number }).localId}`
                : 'Sem local')

        const funcionarioNome =
            (linha as { _funcionarioNome?: string })._funcionarioNome ||
            funcionario?.nome ||
            (linha as { nome?: string }).nome ||
            ''

        const dataLabel =
            (linha as { _dataLabel?: string })._dataLabel || linha.data || ''

        return {
            ...linha,
            _centroCustoNome: centroCustoNome,
            _localNome: localNome,
            _funcionarioNome: funcionarioNome,
            _dataLabel: dataLabel,
        }
    })
}
